import { Button, Dialog, Form, FormContext } from '@hai/ui-react';
import { FormikProps } from 'formik';
import { isNil } from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect } from 'src/js/component/base/form/form-select';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { useListSelectionWithDataStore } from 'src/js/provider/list-selection-with-data-store-provider';
import { constant } from 'src/js/constant';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { validateFormContext } from 'src/js/validator/validator';

import { BufferingModeEnum, DecoderViewModel, MkRange, bufferingValidationSchema } from './decoder-model';
import { DecoderMultiSyncTable, combinedRange } from './decoder-mulitsync-table';

interface Props {
  decoders: DecoderViewModel[];
  onSubmit?: (decoder: DecoderViewModel) => void;
  onCancel?: VoidFunction;
}

export const DecoderSetBufferingModal: React.FunctionComponent<Props> = ({ decoders, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const formRef = useRef(null);

  const [data, setData] = useState<any>({});
  useEffect(() => {
    if (decoders.length > 0) {
      let bufferingMode = BufferingModeEnum.UNSET;

      if (decoders.filter((d) => d.bufferingMode === BufferingModeEnum.FIXED).length === decoders.length) {
        bufferingMode = BufferingModeEnum.FIXED;
      } else if (decoders.filter((d) => d.bufferingMode === BufferingModeEnum.MULTISYNC).length === decoders.length) {
        bufferingMode = BufferingModeEnum.MULTISYNC;
      } else if (decoders.filter((d) => d.bufferingMode === BufferingModeEnum.AUTOMATIC).length === decoders.length) {
        bufferingMode = BufferingModeEnum.AUTOMATIC;
      }

      setData({
        bufferingMode: bufferingMode,
        unsetDelay: undefined,
        bufferingDelay: decoders[0].bufferingDelay,
        multisyncBufferingDelay: decoders[0].multisyncBufferingDelay,
        disabledValue: '',
        decoders: decoders,
      });
    }
  }, [decoders]);

  const dataStore = useListSelectionWithDataStore<DecoderViewModel>();

  useEffect(() => {
    if (!isNil(data.decoders)) {
      dataStore.setData(data.decoders);
    }
  }, [data]);

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') {
      const button = document.getElementById('decoder-set-buffering-form-submit');
      if (!isNilOrEmpty(button)) {
        e.preventDefault();
        button.click();
      }
    }
  };

  const multiSyncValidationRange: MkRange = { min: 0, max: 10000 }; //FIXME range should be written at one place only or from REST API
  const fixedDelayValidationRange: MkRange = { min: 0, max: 3000 };
  const bufferingModeUnset = [{ value: BufferingModeEnum.UNSET, label: t(`decoder.currentSettings`) }];
  const bufferingModes = (data.id >= 0 ? [] : bufferingModeUnset).concat([
    { value: BufferingModeEnum.FIXED, label: t(`decoder.bufferingModes.FIXED`) },
    { value: BufferingModeEnum.MULTISYNC, label: t(`decoder.bufferingModes.MULTISYNC`) },
    { value: BufferingModeEnum.AUTOMATIC, label: t(`decoder.bufferingModes.AUTOMATIC`) },
  ]);

  const content = () => {
    return (
      <Form
        initialValues={data}
        defaultValidation={true}
        handleSubmit={onSubmit}
        restValidationProps={{
          enableReinitialize: true,
          validationSchema: bufferingValidationSchema(multiSyncValidationRange, fixedDelayValidationRange, t),
          innerRef: formRef,
        }}
      >
        <FormContext.Consumer>
          {(formContext: FormikProps<DecoderViewModel>) => {
            const values = formContext.values;
            const name =
              values.bufferingMode === BufferingModeEnum.UNSET
                ? 'unsetDelay'
                : values.bufferingMode === BufferingModeEnum.FIXED
                ? 'bufferingDelay'
                : 'multisyncBufferingDelay';

            return (
              <form className="HaiForm" onKeyDown={handleOnKeyDown}>
                <FormSelect name="bufferingMode" title={t('decoder.fields.bufferingMode')} options={bufferingModes} />
                {values.bufferingMode === BufferingModeEnum.AUTOMATIC ? (
                  <FormTextfield
                    name="disabledValue"
                    title={t('decoder.fields.bufferingDelayMS')}
                    type="number"
                    placeholder={t('general.automatic')}
                    disabled
                  />
                ) : (
                  <FormTextfield
                    name={name}
                    title={t('decoder.fields.bufferingDelayMS')}
                    hint={
                      values.bufferingMode === BufferingModeEnum.MULTISYNC
                        ? combinedRange(dataStore.data)
                        : values.bufferingMode === BufferingModeEnum.FIXED
                        ? `${fixedDelayValidationRange.min} - ${fixedDelayValidationRange.max}`
                        : ''
                    }
                    type="number"
                  />
                )}
                {values.bufferingMode === BufferingModeEnum.MULTISYNC && <DecoderMultiSyncTable />}
                <div className="d-flex justify-content-end hai-mt-8">
                  <Button onClick={onCancel}>{t('general.cancel')}</Button>
                  <Button
                    onClick={() => validateFormContext(formContext, t)}
                    variant="primary"
                    id="decoder-set-buffering-form-submit"
                  >
                    {t('general.apply')}
                  </Button>
                </div>
              </form>
            );
          }}
        </FormContext.Consumer>
      </Form>
    );
  };

  return (
    <Dialog
      accentColor={constant.productColor}
      title={t('decoder.setBuffering')}
      onClose={onCancel}
      show={true}
      dialogType="activity"
      headerIcon="Settings"
      content={content()}
    />
  );
};
